import { AtomErrors } from "./AtomErrors";
import type { ErrorWithDebugInfosLike } from "./ErrorWithDebugInfos";

const INTERNAL_ERROR_MESSAGE_PREFIXES = [
  "internal error: ",
  "internal server error: ",
] as const;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parseSerializedErrorMessage(
  message: string,
): Record<string, unknown> | undefined {
  try {
    const parsed = JSON.parse(message);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function getStringAtPath(
  value: unknown,
  path: readonly string[],
): string | undefined {
  let current: unknown = value;

  for (const segment of path) {
    if (!isRecord(current)) {
      return undefined;
    }
    current = current[segment];
  }

  return typeof current === "string" ? current : undefined;
}

function getMessageRecord(error: unknown): Record<string, unknown> | undefined {
  if (error instanceof Error) {
    return parseSerializedErrorMessage(error.message);
  }

  const message = getStringAtPath(error, ["message"]);
  return message ? parseSerializedErrorMessage(message) : undefined;
}

function getErrorRecordCandidates(
  error: unknown,
): Array<Record<string, unknown>> {
  const candidates: Array<Record<string, unknown>> = [];

  if (isRecord(error)) {
    candidates.push(error);
  }

  const wrappedOriginalError = isRecord(error)
    ? (error.extensions as Record<string, unknown> | undefined)?.originalError
    : undefined;
  if (isRecord(wrappedOriginalError)) {
    candidates.push(wrappedOriginalError);
  }

  const wrappedException = isRecord(error)
    ? (error.extensions as Record<string, unknown> | undefined)?.exception
    : undefined;
  if (isRecord(wrappedException)) {
    candidates.push(wrappedException);
  }

  const messageRecord = getMessageRecord(error);
  if (messageRecord) {
    candidates.push(messageRecord);
  }

  return candidates;
}

export function isErrorWithDebugInfosShape(
  error: unknown,
): error is ErrorWithDebugInfosLike {
  return (
    isRecord(error) &&
    typeof error.type === "string" &&
    typeof error.message === "string" &&
    isRecord(error.debugInfos)
  );
}

export function getDirectErrorType(error: unknown): string | undefined {
  return getStringAtPath(error, ["type"]);
}

export function getDirectInternalType(error: unknown): string | undefined {
  const legacyInternalType = getStringAtPath(error, ["internalType"]);
  if (legacyInternalType) {
    return legacyInternalType;
  }

  return getDirectErrorType(error) === AtomErrors.InternalError
    ? getDirectErrorCode(error)
    : undefined;
}

export function getDirectErrorCode(error: unknown): string | undefined {
  return getStringAtPath(error, ["debugInfos", "errorCode"]);
}

export function getErrorTypeFromUnknown(error: unknown): string | undefined {
  for (const candidate of getErrorRecordCandidates(error)) {
    const type = getDirectErrorType(candidate);
    if (type) {
      return type;
    }
  }

  return undefined;
}

export function getInternalTypeFromUnknown(error: unknown): string | undefined {
  for (const candidate of getErrorRecordCandidates(error)) {
    const internalType = getDirectInternalType(candidate);
    if (internalType) {
      return internalType;
    }
  }

  if (getErrorTypeFromUnknown(error) === AtomErrors.InternalError) {
    return getErrorCodeFromUnknown(error);
  }

  const message = getStringAtPath(error, ["message"]);
  if (!message) {
    return undefined;
  }

  for (const prefix of INTERNAL_ERROR_MESSAGE_PREFIXES) {
    if (message.startsWith(prefix)) {
      return message.slice(prefix.length);
    }
  }

  return undefined;
}

export function getErrorCodeFromUnknown(error: unknown): string | undefined {
  for (const candidate of getErrorRecordCandidates(error)) {
    const errorCode =
      getDirectErrorCode(candidate) ??
      getStringAtPath(candidate, ["errorCode"]) ??
      getStringAtPath(candidate, ["extensions", "errorCode"]) ??
      getStringAtPath(candidate, ["extensions", "code"]) ??
      getStringAtPath(candidate, ["code"]);
    if (errorCode) {
      return errorCode;
    }
  }

  const legacyInternalType = getStringAtPath(error, ["internalType"]);
  if (legacyInternalType) {
    return legacyInternalType;
  }

  const message = getStringAtPath(error, ["message"]);
  if (!message) {
    return undefined;
  }

  for (const prefix of INTERNAL_ERROR_MESSAGE_PREFIXES) {
    if (message.startsWith(prefix)) {
      return message.slice(prefix.length);
    }
  }

  return undefined;
}

export function getErrorMessageFromUnknown(error: unknown): string | undefined {
  for (const candidate of getErrorRecordCandidates(error)) {
    const message = getStringAtPath(candidate, ["message"]);
    if (message) {
      return message;
    }
  }

  return undefined;
}
